import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import { IAdminBooking } from 'app/shared/model/admin-booking.model';
import { IBusiness } from 'app/shared/model/business.model';
import { IBusinessEmployee } from 'app/shared/model/business-employee.model';
import { serializeAxiosError } from 'app/shared/reducers/reducer.utils';

const initialState = {
  loadingBusiness: false,
  loadingBookings: false,
  loadingEmployees: false,
  errorMessage: null as string | null,
  business: null as IBusiness | null,
  bookings: [] as readonly IAdminBooking[],
  bookingsTotalItems: 0,
  employees: [] as readonly IBusinessEmployee[],
};

export type AdminBusinessDetailState = Readonly<typeof initialState>;

const adminBusinessUrl = 'api/admin/business';
const adminBookingsUrl = 'api/admin/bookings';

export const getBusiness = createAsyncThunk(
  'adminBusinessDetail/fetch_business',
  // No single-business admin endpoint yet; reuse the admin filter with an id== RSQL (returns one row).
  async (id: number) => {
    const requestUrl = `${adminBusinessUrl}/filter?page=0&size=1&q=${encodeURIComponent(`id==${id}`)}`;
    return axios.get<{ data: IBusiness[] }>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

interface BookingsParams {
  businessId: number;
  from?: string | null;
  to?: string | null;
  page: number;
  size: number;
  sort: string;
}

export const getBusinessBookings = createAsyncThunk(
  'adminBusinessDetail/fetch_bookings',
  // Single bookings endpoint: scope to the business via RSQL on the path id + optional date range.
  async ({ businessId, from, to, page, size, sort }: BookingsParams) => {
    const parts = [`service.employee.business.id==${businessId}`];
    if (from) parts.push(`startDateTime=ge='${from}T00:00:00.000Z'`);
    if (to) parts.push(`startDateTime=le='${to}T23:59:59.999Z'`);
    const q = encodeURIComponent(parts.join(' and '));
    const requestUrl = `${adminBookingsUrl}/filter?page=${page}&size=${size}&sort=${sort}&q=${q}`;
    return axios.get<{ data: IAdminBooking[]; pagination: { totalItems: number } }>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const getEmployees = createAsyncThunk(
  'adminBusinessDetail/fetch_employees',
  // Reuses the (public) business-employees filter; admin-scoped variant is a future improvement.
  // Only the EMPLOYEE role belongs on the staff list — a MANAGER-only membership (e.g. the owner,
  // who isn't a service provider) must not show up here. Include every status (active or not),
  // matching the manager-facing staff list semantics.
  async (slug: string) => {
    const requestUrl = `api/business-employees/${slug}/filter?size=100&q=${encodeURIComponent('role==EMPLOYEE')}`;
    return axios.get<{ data: IBusinessEmployee[] }>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const AdminBusinessDetailSlice = createSlice({
  name: 'adminBusinessDetail',
  initialState: initialState as AdminBusinessDetailState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getBusiness.pending, state => {
        state.loadingBusiness = true;
        state.errorMessage = null;
      })
      .addCase(getBusiness.fulfilled, (state, action) => {
        state.loadingBusiness = false;
        state.business = action.payload.data.data?.[0] ?? null;
      })
      .addCase(getBusiness.rejected, (state, action) => {
        state.loadingBusiness = false;
        state.errorMessage = action.error.message!;
      })
      .addCase(getBusinessBookings.pending, state => {
        state.loadingBookings = true;
      })
      .addCase(getBusinessBookings.fulfilled, (state, action) => {
        state.loadingBookings = false;
        state.bookings = action.payload.data.data;
        state.bookingsTotalItems = action.payload.data.pagination.totalItems;
      })
      .addCase(getBusinessBookings.rejected, (state, action) => {
        state.loadingBookings = false;
        state.errorMessage = action.error.message!;
      })
      .addCase(getEmployees.pending, state => {
        state.loadingEmployees = true;
      })
      .addCase(getEmployees.fulfilled, (state, action) => {
        state.loadingEmployees = false;
        state.employees = action.payload.data.data;
      })
      .addCase(getEmployees.rejected, (state, action) => {
        state.loadingEmployees = false;
        state.errorMessage = action.error.message!;
      });
  },
});

export const { reset } = AdminBusinessDetailSlice.actions;

export default AdminBusinessDetailSlice.reducer;
